/**
 * This component shares the same data as the aggregated data table in the main view.
 */

import { Box, Typography } from '@mui/material';
import { CompetitorShareOfVoiceAggregatedDataItemFragmentBcShareOfVoiceTableFragment } from 'graphql/generated';
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';
import {
  AggregationType,
  AggregationTypeLabelMap,
} from './BrandCompetitorsShareOfVoiceView';

export type BrandCompetitorsShareOfVoiceBarChartProps = {
  aggregationType: AggregationType;
  selectedItems: CompetitorShareOfVoiceAggregatedDataItemFragmentBcShareOfVoiceTableFragment[];
  itemIdToColorMap: Record<string, string>;
  activeItemId?: string;
  onCompetitorClick?: (competitorTrackingBrandId: string) => void;
};

export const BrandCompetitorsShareOfVoiceBarChart = (
  props: BrandCompetitorsShareOfVoiceBarChartProps,
) => {
  const {
    selectedItems,
    itemIdToColorMap,
    aggregationType = AggregationType.ShareOfVoice,
    activeItemId,
    onCompetitorClick,
  } = props;

  const data = useMemo(() => {
    switch (aggregationType) {
      case AggregationType.ShareOfVoice: {
        // If aggregation type is share of voice, we want the sum of all selected items
        // to always amount to 100%. Currently the data is on ALL items, so we need to scale the value up
        const totalShareOfVoice =
          selectedItems.reduce(
            (acc, item) => acc + item.aggregatedData.shareOfVoice,
            0,
          ) || 1;

        return selectedItems.map((item) => ({
          id: item.base.brand.id,
          name: item.base.brand.name,
          color: itemIdToColorMap[item.id],
          value: Number(
            (item.aggregatedData.shareOfVoice / totalShareOfVoice) * 100,
          ),
        }));
      }
      case AggregationType.MentionCount:
        return selectedItems.map((item) => ({
          id: item.base.brand.id,
          name: item.base.brand.name,
          color: itemIdToColorMap[item.id],
          value: item.aggregatedData.mentionCount,
        }));
      case AggregationType.ViewCount:
        return selectedItems.map((item) => ({
          id: item.base.brand.id,
          name: item.base.brand.name,
          color: itemIdToColorMap[item.id],
          value: item.aggregatedData.viewCount,
        }));
      case AggregationType.TotalEngagement:
        return selectedItems.map((item) => ({
          id: item.base.brand.id,
          name: item.base.brand.name,
          color: itemIdToColorMap[item.id],
          value: item.aggregatedData.totalEngagement,
        }));
      case AggregationType.EngagementRate:
        return selectedItems.map((item) => ({
          id: item.base.brand.id,
          name: item.base.brand.name,
          color: itemIdToColorMap[item.id],
          value: Number(item.aggregatedData.engagementRate * 100),
        }));
      default:
        return [];
    }
  }, [selectedItems, aggregationType, itemIdToColorMap]);

  return (
    <Box
      sx={{
        display: 'flex',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 20,
        }}
      >
        <Typography
          variant="headline-sm"
          color={theme.colors?.utility[700]}
          sx={{
            textAlign: 'center',
            writingMode: 'sideways-lr',
            height: 300,
          }}
        >
          {AggregationTypeLabelMap[aggregationType]}
        </Typography>
      </Box>
      <ResponsiveContainer
        width="100%"
        height={300}
        style={{
          flex: 1,
        }}
      >
        <BarChart data={data}>
          <CartesianGrid
            vertical={false}
            stroke={theme.colors?.utility[400]}
            strokeDasharray="4"
          />
          <XAxis
            dataKey="name"
            tickLine={false}
            axisLine={false}
            style={{
              ...theme.typography['headline-sm'],
              color: theme.colors?.utility[700],
            }}
            interval={0}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            dataKey="value"
            style={{
              ...theme.typography['headline-sm'],
              color: theme.colors?.utility[700],
            }}
            tickFormatter={(value) => {
              switch (aggregationType) {
                case AggregationType.ShareOfVoice:
                case AggregationType.EngagementRate:
                  return `${value.toFixed(0)}%`;
                default:
                  return formatBigNumber(Number(value));
              }
            }}
            max={
              aggregationType === AggregationType.ShareOfVoice ? 100 : undefined
            }
          />
          <Bar dataKey="value" maxBarSize={75} radius={12}>
            {data.map((item) => (
              <Cell
                key={item.name}
                fill={item.color}
                opacity={
                  activeItemId ? (activeItemId === item.id ? 1 : 0.5) : 1
                }
                onClick={() => {
                  onCompetitorClick?.(item.id);
                }}
                style={{
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                }}
              />
            ))}
          </Bar>
          <Tooltip
            cursor={false}
            // eslint-disable-next-line
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                let value = payload[0].value;
                const color = payload[0].payload.color;

                switch (aggregationType) {
                  case AggregationType.ShareOfVoice:
                  case AggregationType.EngagementRate:
                    value = `${Number(value).toFixed(2)}%`;
                    break;
                  default:
                    value = formatBigNumber(Number(value));
                }

                return (
                  <Box
                    sx={{
                      borderRadius: 2.5,
                      border: `1px solid ${theme.colors?.utility[100]}`,
                      boxShadow: '0px 2.56px 12px 0px #00000014',
                      padding: 4,
                      bgcolor: theme.colors?.primary.white,
                    }}
                  >
                    <Typography
                      variant="headline-sm"
                      sx={{
                        color,
                        fontSize: 20,
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                );
              }

              return null;
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
